import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { ExpenseCategory, getCategoryDisplayLabel } from '../../types/expenseCategory';

interface CostCenter {
  id: string;
  name: string;
}

interface GroupedCategoryOption {
  category: ExpenseCategory;
  costCenterName: string;
  displayLabel: string;
}

interface GroupedCategoryDropdownProps {
  /** Filter categories by business type ID */
  typeId?: string;
  /** Filter categories by cost center ID */
  costCenterId?: string;
  /** Filter categories by nature */
  nature?: 'routine' | 'stock' | 'capital' | 'payroll' | 'admin';
  /** Fallback nature if none provided */
  fallbackNature?: 'routine' | 'stock' | 'capital' | 'payroll' | 'admin';
  /** Currently selected category ID */
  value?: string;
  /** Callback when category is selected */
  onSelect: (categoryId: string, categoryMeta: {
    category: ExpenseCategory;
    costCenterName: string;
    displayLabel: string;
    needsNameField: boolean;
  }) => void;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
}

/**
 * GroupedCategoryDropdown - Reusable dropdown component for selecting categories
 * 
 * Features:
 * - Filters categories by typeId, costCenterId, and nature
 * - Shows display as "Category Name (Cost Center)"
 * - Simple dropdown interface (no search input)
 * - Returns selected categoryId and metadata
 */
export default function GroupedCategoryDropdown({
  typeId,
  costCenterId,
  nature,
  fallbackNature,
  value,
  onSelect,
  placeholder = "Select category...",
  disabled = false,
}: GroupedCategoryDropdownProps) {

  // Fetch cost centers for display names
  const { data: costCenters = [] } = useQuery<CostCenter[]>({
    queryKey: ['costCenters'],
    queryFn: async () => {
      const costCenterSnapshot = await getDocs(collection(db, 'costCenters'));
      return costCenterSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CostCenter[];
    },
  });

  // Fetch categories with filters
  const { data: categories = [], isLoading, error } = useQuery<ExpenseCategory[]>({
    queryKey: ['categoriesForDropdown', typeId, costCenterId, nature || fallbackNature],
    queryFn: async () => {
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      const allCategories = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ExpenseCategory[];
      
      // Filter using correct field names that match the database schema
      return allCategories.filter(category => {
        // Check if active (database uses 'active' field, not 'isActive')
        if (category.active === false) return false;
        
        // Filter by type (database uses 'type' field, not 'typeId')
        if (typeId && category.type !== typeId) return false;
        
        // Filter by costCenterId (this field name is correct)
        if (costCenterId && category.costCenterId !== costCenterId) return false;
        
        // Filter by nature
        if ((nature || fallbackNature) && category.nature !== (nature || fallbackNature)) return false;
        
        return true;
      });
    },
    enabled: true,
  });

  // Process categories into grouped options
  const categoryOptions = useMemo(() => {
    if (!categories.length || !costCenters.length) {
      return [];
    }

    return categories.map(category => {
      const costCenter = costCenters.find(cc => cc.id === category.costCenterId);
      const costCenterName = costCenter?.name || 'Unknown Cost Center';
      const displayLabel = getCategoryDisplayLabel(category, costCenterName);
      
      return {
        category,
        costCenterName,
        displayLabel,
      };
    }).sort((a, b) => {
      // Sort by cost center first, then by category name
      if (a.costCenterName === b.costCenterName) {
        return a.category.name.localeCompare(b.category.name);
      }
      return a.costCenterName.localeCompare(b.costCenterName);
    });
  }, [categories, costCenters, typeId, costCenterId, nature, fallbackNature]);

  // Find selected category for display
  const selectedCategory = useMemo(() => {
    if (!value) return null;
    return categoryOptions.find(option => option.category.id === value);
  }, [value, categoryOptions]);

  const handleSelect = (categoryOption: GroupedCategoryOption) => {
    onSelect(categoryOption.category.id, {
      category: categoryOption.category,
      costCenterName: categoryOption.costCenterName,
      displayLabel: categoryOption.displayLabel,
      needsNameField: categoryOption.category.requiresNameField || false,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-w-[160px] flex-1">
        <div className="border px-2 py-1 rounded bg-gray-50 text-gray-500">
          Loading categories...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-w-[160px] flex-1">
        <div className="border px-2 py-1 rounded bg-error-50 text-error-600 text-sm">
          Error loading categories
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-w-[160px] flex-1">
      {/* Category Select */}
      <select
        value={value || ""}
        onChange={(e) => {
          const selectedOption = categoryOptions.find(option => option.category.id === e.target.value);
          if (selectedOption) {
            handleSelect(selectedOption);
          }
        }}
        className={`w-full border bg-card px-3 py-2 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 min-h-[48px] ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
        disabled={disabled}
        title="Select expense category"
        aria-label="Select expense category"
      >
        <option value="">{placeholder}</option>
        {categoryOptions.map((option) => (
          <option key={option.category.id} value={option.category.id}>
            {option.displayLabel}
          </option>
        ))}
      </select>
      
      {/* Selected Category Info */}
      {selectedCategory && (
        <div className="text-xs text-gray-600 mt-1">
          {selectedCategory.category.requiresNameField && (
            <span className="text-warning-600">• Additional name field required</span>
          )}
        </div>
      )}
      
      {/* No Categories Available */}
      {categoryOptions.length === 0 && (
        <div className="text-xs text-amber-600 mt-1">
          No categories available
        </div>
      )}
    </div>
  );
}
